/**
 * SanPlumb — Paleta de colores para Three.js
 *
 * Los valores son enteros hex (formato Three.js, ej. 0x00C2FF) y se mapean
 * 1:1 con las variables CSS de `App.css` §XIII. Si CLAUDE.md §XIII cambia,
 * este archivo debe actualizarse en el mismo commit (§XVI.6).
 *
 * Origen de migración: legacy `sanplumb-mvp__1_.html` líneas 594-601.
 */

import type { AccessoryColor, System } from '../../types';

/** Paleta completa para usar con `THREE.MeshPhongMaterial({ color })`. */
export const COLORS = {
  /** AF — Agua Fría — cian eléctrico (CSS: --af). */
  AF: 0x00c2ff,
  /** AC — Agua Caliente — rojo brillante (CSS: --ac). */
  AC: 0xff4040,

  /** Accesorios AF — ámbar (CSS: --c3). */
  acc: 0xf4a261,
  /** Accesorios AC — ámbar cálido (CSS: --c4). */
  acc_ac: 0xff8c42,

  /** Válvulas — dorado (CSS: --c5). Mismo color para AF y AC. */
  valve: 0xffb800,
  valve_ac: 0xffb800,

  /** Reducciones — violeta (CSS: --red). */
  reduction: 0x8b5cf6,
  reduction_ac: 0x8b5cf6,

  /** Cotas / medidas 3D — amarillo técnico (CSS: --dim-col). */
  dimension: 0xffd60a,

  /** Color de aparatos sanitarios (renderizados con opacidad 0.85 §III). */
  sanitary: 0x091d34,
  /** Color secundario del wireframe de sanitarios. */
  sanitaryWire: 0x0d3a5a,

  /** Color del piso del visor. */
  floor: 0x060f1e,
  /** Color de las líneas de grilla del piso. */
  floorGrid: 0x0c2238,

  /** Color de las aristas del muro principal. */
  wallEdge: 0x1a3a5a,
  /** Color de las aristas del muro secundario (techo, etc.). */
  wallEdgeDim: 0x2a5a7a,
} as const;

/**
 * Devuelve el color hex para el sistema (AF/AC) de una tubería.
 * Función helper para mantener `system → color` consistente.
 */
export function pipeColor(system: System): number {
  return system === 'AC' ? COLORS.AC : COLORS.AF;
}

/**
 * Devuelve el color hex para un accesorio dado su `color` semántico.
 * Mapea el discriminante de tipo a la entrada correspondiente en COLORS.
 */
export function accessoryColor(c: AccessoryColor): number {
  switch (c) {
    case 'acc':
      return COLORS.acc;
    case 'acc_ac':
      return COLORS.acc_ac;
    case 'valve':
      return COLORS.valve;
    case 'valve_ac':
      return COLORS.valve_ac;
    case 'reduction':
      return COLORS.reduction;
    case 'reduction_ac':
      return COLORS.reduction_ac;
    case 'llave_af':
      return COLORS.AF;
    case 'llave_ac':
      return COLORS.AC;
  }
}
